import React from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from './Icons';

/**
 * Right-anchored slide-in drawer for detail and edit panels (Order details,
 * Edit Menu Item, etc.). MUI flips anchor automatically under RTL because the
 * theme carries `direction: 'rtl'`. On phone, the drawer goes full-screen.
 *
 * Props:
 *   open, onClose       — controlled
 *   title               — header text
 *   actions             — sticky footer node (Save / Cancel buttons, etc.)
 *   width               — desktop width (default 480)
 *   children            — body content
 */
const RightDrawer = ({
  open,
  onClose,
  title,
  actions,
  width = 480,
  ariaLabel,
  children,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: fullScreen ? '100vw' : width,
          maxWidth: '100vw',
          borderStartStartRadius: fullScreen ? 0 : 20,
          borderEndStartRadius: fullScreen ? 0 : 20,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      ModalProps={{
        keepMounted: false,
        'aria-label': ariaLabel || title || 'Detail panel',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: 2.5,
          flexShrink: 0,
        }}
      >
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon size={20} />
        </IconButton>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontFamily: '"Plus Jakarta Sans", "IBM Plex Sans Arabic", Inter, sans-serif',
            fontWeight: 700,
            color: 'text.primary',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Divider />

      {/* Body — scrollable */}
      <Box sx={{ flex: 1, overflowY: 'auto', padding: 2.5 }}>
        {children}
      </Box>

      {/* Sticky footer */}
      {actions && (
        <>
          <Divider />
          <Box
            sx={{
              padding: 2.5,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              flexShrink: 0,
              backgroundColor: 'background.paper',
            }}
          >
            {actions}
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default RightDrawer;
